import React, { useCallback, useEffect, useReducer } from "react";
import { EXERCISE_TYPES } from "../../libs/exercises/types";
import { useGetAllExercisesQuery, useGetAllExerciseContainersQuery } from "../../libs/exercises/api";
import { useGetAllProgressionTemplatesQuery, useGetAllProgressionContainersQuery } from "../../libs/progression/api";
import { useGetSpecialCategoriesQuery } from "../../libs/specialCategory/api";
import { useAppDispatch } from "../../store";
import { addExercise } from "../../libs/workout/slice";
import { unique } from "../../libs/utils/array";
import { CHECK_NOTES_TEMPLATE, CHECK_DEFAULT_SETS } from "../ExercisesTable/CheckExercisesSection";
import { Skeleton } from "../Skeleton";
import { StepExerciseType } from "./WizardSteps/StepExerciseType";
import { StepExercisePicker } from "./WizardSteps/StepExercisePicker";
import { StepProgressionPicker } from "./WizardSteps/StepProgressionPicker";
import { StepConfirm } from "./WizardSteps/StepConfirm";
import { ProgressionTemplate } from "../../libs/progression/types";

// ── Types ────────────────────────────────────────────────────────────────────

type ExerciseWizardModalProps = {
  dayIndex: number | undefined;
  exToCloneIndex?: number;
  exType?: keyof typeof EXERCISE_TYPES;
  setOpenModal: (
    state:
      | { dayIndex: number; exToCloneIndex?: number; exType?: keyof typeof EXERCISE_TYPES }
      | undefined
  ) => void;
};

type WizardState = {
  step: 1 | 2 | 3 | 4;
  exerciseType: string;
  selectedExerciseId: string | null;
  supersetExercises: { id: string; progressionId: string | null }[];
  selectedProgressionId: string | null;
  deleteClone: boolean;
  keepNotes: boolean;
};

type WizardAction =
  | { type: "SET_STEP"; step: 1 | 2 | 3 | 4 }
  | { type: "SET_EXERCISE_TYPE"; exerciseType: string }
  | { type: "SELECT_EXERCISE"; id: string }
  | { type: "ADD_SUPERSET_EXERCISE"; id: string }
  | { type: "REMOVE_SUPERSET_EXERCISE"; id: string }
  | { type: "SELECT_PROGRESSION"; id: string | null }
  | { type: "UPDATE_SUPERSET_PROGRESSION"; exerciseId: string; progressionId: string | null }
  | { type: "TOGGLE_DELETE_CLONE" }
  | { type: "TOGGLE_KEEP_NOTES" };

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step };
    case "SET_EXERCISE_TYPE":
      return {
        ...state,
        exerciseType: action.exerciseType,
        // Reset selections when type changes
        selectedExerciseId: null,
        supersetExercises: [],
        selectedProgressionId: null,
      };
    case "SELECT_EXERCISE":
      return { ...state, selectedExerciseId: action.id };
    case "ADD_SUPERSET_EXERCISE":
      if (state.supersetExercises.length >= 5) return state;
      if (state.supersetExercises.some((se) => se.id === action.id)) return state;
      return {
        ...state,
        supersetExercises: [
          ...state.supersetExercises,
          { id: action.id, progressionId: null },
        ],
      };
    case "REMOVE_SUPERSET_EXERCISE":
      return {
        ...state,
        supersetExercises: state.supersetExercises.filter(
          (se) => se.id !== action.id
        ),
      };
    case "SELECT_PROGRESSION":
      return { ...state, selectedProgressionId: action.id };
    case "UPDATE_SUPERSET_PROGRESSION":
      return {
        ...state,
        supersetExercises: state.supersetExercises.map((se) =>
          se.id === action.exerciseId
            ? { ...se, progressionId: action.progressionId }
            : se
        ),
      };
    case "TOGGLE_DELETE_CLONE":
      return { ...state, deleteClone: !state.deleteClone };
    case "TOGGLE_KEEP_NOTES":
      return { ...state, keepNotes: !state.keepNotes };
    default:
      return state;
  }
}

// ── Step labels for stepper ──────────────────────────────────────────────────

const STEP_LABELS = [
  { num: 1, label: "Tipo" },
  { num: 2, label: "Esercizio" },
  { num: 3, label: "Progressione" },
  { num: 4, label: "Conferma" },
] as const;

// ── Component ────────────────────────────────────────────────────────────────

export const ExerciseWizardModal: React.FC<ExerciseWizardModalProps> = ({
  dayIndex,
  exToCloneIndex,
  exType,
  setOpenModal,
}) => {
  const dispatch = useAppDispatch();

  // RTK Query hooks
  const { data: exercises, isLoading: exLoading } = useGetAllExercisesQuery();
  const { data: exerciseContainers, isLoading: ecLoading } = useGetAllExerciseContainersQuery();
  const { data: progressionTemplates, isLoading: ptLoading } = useGetAllProgressionTemplatesQuery();
  const { data: progressionContainers, isLoading: pcLoading } = useGetAllProgressionContainersQuery();
  const { data: specialCategories } = useGetSpecialCategoriesQuery();

  const checkCategory = specialCategories?.find((c) =>
    c.name.toUpperCase().includes("CHECK")
  );

  const initialStep: 1 | 2 = exType ? 2 : 1;
  const [state, wizardDispatch] = useReducer(wizardReducer, {
    step: initialStep,
    exerciseType: exType || EXERCISE_TYPES.DEFAULT,
    selectedExerciseId: null,
    supersetExercises: [],
    selectedProgressionId: null,
    deleteClone: false,
    keepNotes: false,
  });

  const isOpen = dayIndex !== undefined;

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenModal(undefined);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, setOpenModal]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Helper
  const findTemplate = useCallback(
    (id: string | null): ProgressionTemplate | undefined => {
      if (!id || !progressionTemplates) return undefined;
      return progressionTemplates.find((t) => t.id + "" === id + "");
    },
    [progressionTemplates]
  );

  // ── Navigation ──────────────────────────────────────────────────────────

  const skipProgressionStep =
    state.exerciseType === EXERCISE_TYPES.STRIPPING ||
    state.exerciseType === "CHECK";

  const canGoNext = (): boolean => {
    switch (state.step) {
      case 1:
        return !!state.exerciseType;
      case 2:
        if (state.exerciseType === EXERCISE_TYPES.SUPERSET) {
          return state.supersetExercises.length >= 2;
        }
        return !!state.selectedExerciseId;
      case 3:
        return true; // progression is always optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  const goNext = () => {
    if (!canGoNext()) return;
    if (state.step === 1) {
      wizardDispatch({ type: "SET_STEP", step: 2 });
    } else if (state.step === 2) {
      if (skipProgressionStep) {
        wizardDispatch({ type: "SET_STEP", step: 4 });
      } else {
        wizardDispatch({ type: "SET_STEP", step: 3 });
      }
    } else if (state.step === 3) {
      wizardDispatch({ type: "SET_STEP", step: 4 });
    }
  };

  const goBack = () => {
    if (state.step === 2) {
      if (!exType) wizardDispatch({ type: "SET_STEP", step: 1 });
    } else if (state.step === 3) {
      wizardDispatch({ type: "SET_STEP", step: 2 });
    } else if (state.step === 4) {
      if (skipProgressionStep) {
        wizardDispatch({ type: "SET_STEP", step: 2 });
      } else {
        wizardDispatch({ type: "SET_STEP", step: 3 });
      }
    }
  };

  // ── Confirm handler (replicates ExerciseForm.onSubmit logic) ──────────

  const handleConfirm = () => {
    if (!exercises || dayIndex === undefined) return;

    type ExToAdd = {
      id: string | null;
      name: string | null;
      videoUrls: string[];
      tags: string[];
      subExercises?: { id: string; name: string }[];
      specialCategoryId?: number;
      checkDistrict?: string;
      notes?: string;
    };

    let exToAdd: ExToAdd = {
      id: null,
      name: null,
      videoUrls: [],
      tags: [],
      subExercises: [],
    };

    let progressionTemplate: ProgressionTemplate | undefined;
    let progressionTemplates_: ProgressionTemplate[] | undefined;

    if (state.exerciseType === EXERCISE_TYPES.SUPERSET) {
      // SUPERSET: concatenate exercises
      state.supersetExercises.forEach((se) => {
        const exercise = exercises.find((ex) => ex.id + "" === se.id + "");
        if (!exercise) return;
        exToAdd.id = exToAdd.id
          ? `${exToAdd.id}-${exercise.id}`
          : `${exercise.id}`;
        exToAdd.name = !exToAdd.name
          ? `SUPERSET - ${exercise.name}`
          : `${exToAdd.name} + ${exercise.name}`;
        exToAdd.videoUrls = [...exToAdd.videoUrls, ...exercise.videoUrls];
        exToAdd.tags = [...exToAdd.tags, ...exercise.tags];
        exToAdd.subExercises?.push({
          id: exercise.id + "",
          name: exercise.name,
        });
      });

      const templates = state.supersetExercises.map((se) =>
        findTemplate(se.progressionId)
      );
      const hasAnyTemplate = templates.some(Boolean);
      if (hasAnyTemplate) {
        const fallback = templates.find(Boolean)!;
        progressionTemplates_ = templates.map((t) => t ?? fallback);
      }
    } else if (state.exerciseType === EXERCISE_TYPES.STRIPPING) {
      const exercise = exercises.find((ex) => ex.id === state.selectedExerciseId);
      if (!exercise) return;
      exToAdd = {
        ...exercise,
        name: "STRIPPING - " + exercise.name,
        subExercises: undefined,
      };
    } else {
      // DEFAULT or CHECK
      const exercise = exercises.find((ex) => ex.id === state.selectedExerciseId);
      if (!exercise) return;
      exToAdd = { ...exercise, subExercises: undefined };
      progressionTemplate = findTemplate(state.selectedProgressionId);
    }

    if (!exToAdd.id || !exToAdd.name) return;

    const isCheck = !!exToAdd.specialCategoryId && !!exToAdd.checkDistrict;

    dispatch(
      addExercise({
        dayIndex,
        exToCloneIndex,
        removeExToClone: state.deleteClone,
        keepNotes: state.keepNotes,
        exercise: {
          id: exToAdd.id,
          name: exToAdd.name,
          type: (state.exerciseType === "CHECK" ? "DEFAULT" : state.exerciseType) as unknown as keyof typeof EXERCISE_TYPES,
          videoUrls: exToAdd.videoUrls?.filter(unique),
          tags: exToAdd.tags?.filter(unique),
          subExercises: exToAdd.subExercises,
          specialCategoryId: exToAdd.specialCategoryId,
          checkDistrict: exToAdd.checkDistrict,
          sets: isCheck ? CHECK_DEFAULT_SETS : [],
          completed: false,
          notes: isCheck ? (exToAdd.notes || CHECK_NOTES_TEMPLATE) : "",
        },
        progressionTemplate,
        progressionTemplates: progressionTemplates_,
      })
    );

    setOpenModal(undefined);
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const isLoading = exLoading || ecLoading || ptLoading || pcLoading;

  // Determine visible steps (skip step 1 if exType provided)
  const visibleSteps = exType
    ? STEP_LABELS.filter((s) => s.num !== 1)
    : STEP_LABELS;

  // For the stepper: determine completed status
  const getStepStatus = (stepNum: number): "completed" | "active" | "future" => {
    if (stepNum < state.step) return "completed";
    if (stepNum === state.step) return "active";
    return "future";
  };

  // Resolve data for StepConfirm
  const selectedExercise =
    state.selectedExerciseId && exercises
      ? exercises.find((e) => e.id === state.selectedExerciseId) ?? null
      : null;
  const selectedProgression = findTemplate(state.selectedProgressionId) ?? null;
  const supersetForConfirm = state.supersetExercises.map((se) => ({
    exercise: exercises?.find((e) => e.id === se.id) ?? null,
    progression: findTemplate(se.progressionId) ?? null,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[95vw] max-w-6xl h-[85vh] flex flex-col overflow-hidden">
        {/* ── Header: Stepper + Close ── */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* Step indicator */}
            <div className="flex items-center gap-3">
              {visibleSteps.map((s, i) => {
                const status = getStepStatus(s.num);
                return (
                  <React.Fragment key={s.num}>
                    {i > 0 && (
                      <div
                        className={`w-8 h-0.5 ${
                          status !== "future"
                            ? "bg-blue-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                    )}
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                          status === "completed"
                            ? "bg-blue-500 text-white"
                            : status === "active"
                            ? "bg-blue-500 text-white"
                            : "bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        {status === "completed" ? (
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          s.num
                        )}
                      </div>
                      <span
                        className={`text-sm hidden sm:inline ${
                          status === "active"
                            ? "text-blue-600 dark:text-blue-400 font-medium"
                            : status === "completed"
                            ? "text-gray-600 dark:text-gray-300"
                            : "text-gray-400 dark:text-gray-500"
                        }`}
                      >
                        {s.label}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Close button */}
            <button
              onClick={() => setOpenModal(undefined)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <Skeleton />
          ) : (
            <>
              {state.step === 1 && (
                <StepExerciseType
                  selectedType={state.exerciseType}
                  onSelectType={(t) =>
                    wizardDispatch({ type: "SET_EXERCISE_TYPE", exerciseType: t })
                  }
                />
              )}
              {state.step === 2 && (
                <StepExercisePicker
                  exercises={exercises ?? []}
                  containers={exerciseContainers ?? []}
                  exerciseType={state.exerciseType}
                  selectedExerciseId={state.selectedExerciseId}
                  onSelectExercise={(id) =>
                    wizardDispatch({ type: "SELECT_EXERCISE", id })
                  }
                  supersetExercises={state.supersetExercises}
                  onAddSupersetExercise={(id) =>
                    wizardDispatch({ type: "ADD_SUPERSET_EXERCISE", id })
                  }
                  onRemoveSupersetExercise={(id) =>
                    wizardDispatch({ type: "REMOVE_SUPERSET_EXERCISE", id })
                  }
                  checkCategoryId={checkCategory?.id}
                />
              )}
              {state.step === 3 && (
                <StepProgressionPicker
                  progressionTemplates={progressionTemplates ?? []}
                  progressionContainers={progressionContainers ?? []}
                  exerciseType={state.exerciseType}
                  exercises={exercises ?? []}
                  selectedProgressionId={state.selectedProgressionId}
                  onSelectProgression={(id) =>
                    wizardDispatch({ type: "SELECT_PROGRESSION", id })
                  }
                  supersetExercises={state.supersetExercises}
                  onUpdateSupersetProgression={(exerciseId, progressionId) =>
                    wizardDispatch({
                      type: "UPDATE_SUPERSET_PROGRESSION",
                      exerciseId,
                      progressionId,
                    })
                  }
                />
              )}
              {state.step === 4 && (
                <StepConfirm
                  exerciseType={state.exerciseType}
                  selectedExercise={selectedExercise}
                  selectedProgression={selectedProgression}
                  supersetExercises={supersetForConfirm}
                  exToCloneIndex={exToCloneIndex}
                  deleteClone={state.deleteClone}
                  keepNotes={state.keepNotes}
                  onToggleDeleteClone={() =>
                    wizardDispatch({ type: "TOGGLE_DELETE_CLONE" })
                  }
                  onToggleKeepNotes={() =>
                    wizardDispatch({ type: "TOGGLE_KEEP_NOTES" })
                  }
                />
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          {/* Back button */}
          <div>
            {state.step > (exType ? 2 : 1) && (
              <button
                onClick={goBack}
                className="py-2.5 px-5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                &larr; Indietro
              </button>
            )}
          </div>

          {/* Next / Confirm button */}
          <div>
            {state.step < 4 ? (
              <button
                onClick={goNext}
                disabled={!canGoNext()}
                className="py-2.5 px-6 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Avanti &rarr;
              </button>
            ) : (
              <button
                onClick={handleConfirm}
                className="py-2.5 px-6 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition-colors"
              >
                Aggiungi esercizio
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
